import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch, translateApiError } from '../../shared/api/client';

/**
 * The two pages an e-mail link lands on.
 *
 * These exist because the links the API sends were pointing at routes the client
 * never defined: `POST /auth/verify-email` and `POST /auth/reset-password` were
 * implemented and the messages were correct, but `/verify-email?token=…` and
 * `/reset-password?token=…` fell through to the 404 page. A confirmation link
 * that leads to "page not found" is worse than no link, because the user cannot
 * tell whether their account was confirmed.
 *
 * Both pages follow the same three rules:
 *
 *   1. **The token comes from the URL** and is posted exactly once. The server
 *      marks it consumed, so a reload must not be presented as a fresh attempt.
 *   2. **The outcome is stated in words**, not implied by a redirect: the server
 *      distinguishes "expired" from "already used" from "confirmed", and the page
 *      repeats what it was told.
 *   3. **Errors are translated** like everywhere else (section 10.2).
 */

export function VerifyEmailPage(): React.ReactNode {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<'working' | 'done' | 'failed'>('working');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState('failed');
      setMessage(t('auth.verify_token_missing'));
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const result = await apiFetch<{ code: string; message: string }>('/auth/verify-email', {
          method: 'POST',
          json: { token },
        });
        if (cancelled) return;
        setMessage(t(result.code, { defaultValue: result.message }));
        setState('done');
      } catch (cause) {
        if (cancelled) return;
        setMessage(translateApiError(cause, t));
        setState('failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, t]);

  return (
    <Outcome
      title={
        state === 'done'
          ? t('auth.verifyDoneTitle')
          : state === 'failed'
            ? t('auth.verifyFailedTitle')
            : t('auth.verifyWorkingTitle')
      }
      message={message}
      tone={state === 'failed' ? 'error' : 'info'}
      busy={state === 'working'}
    >
      {state === 'done' && (
        <Link to="/signin" className="text-brand-600 hover:underline">
          {t('auth.signInAction')}
        </Link>
      )}
      {state === 'failed' && (
        <Link to="/signup" className="text-brand-600 hover:underline">
          {t('auth.signUpAction')}
        </Link>
      )}
    </Outcome>
  );
}

export function ResetPasswordPage(): React.ReactNode {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    // Checked here rather than by the server so the user is not told "the link
    // is invalid" for what is a mistyped confirmation.
    if (password !== repeat) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    setBusy(true);
    try {
      const result = await apiFetch<{ code: string; message: string }>('/auth/reset-password', {
        method: 'POST',
        json: { token, password },
      });
      setDone(true);
      setError(null);
      setPassword('');
      setRepeat('');
      // The success message is shown on the same screen, with the server's
      // wording, because it is also the news that the other sessions ended.
      setError(t(result.code, { defaultValue: result.message }));
    } catch (cause) {
      setError(translateApiError(cause, t));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <Outcome
        title={t('auth.resetFailedTitle')}
        message={t('auth.reset_token_missing')}
        tone="error"
      >
        <Link to="/signin" className="text-brand-600 hover:underline">
          {t('auth.signInAction')}
        </Link>
      </Outcome>
    );
  }

  if (done) {
    return (
      <Outcome title={t('auth.resetDoneTitle')} message={error} tone="info">
        <Link to="/signin" className="text-brand-600 hover:underline">
          {t('auth.signInAction')}
        </Link>
      </Outcome>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 py-14">
      <h1 className="text-2xl font-bold">{t('auth.resetTitle')}</h1>
      <p className="mt-2 text-sm text-[var(--color-muted-fg)]">{t('auth.resetSubtitle')}</p>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <label htmlFor="new-password" className="block text-sm font-medium">
            {t('auth.newPassword')}
          </label>
          <input
            id="new-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            required
            className="mt-1 h-10 w-full rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/30"
          />
          <p className="mt-1 text-xs text-[var(--color-muted-fg)]">
            {t('auth.passwordHint', { count: 10 })}
          </p>
        </div>

        <div>
          <label htmlFor="repeat-password" className="block text-sm font-medium">
            {t('auth.confirmPassword')}
          </label>
          <input
            id="repeat-password"
            type="password"
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
            autoComplete="new-password"
            required
            className="mt-1 h-10 w-full rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/30"
          />
        </div>

        {error && (
          <p role="alert" className="rounded-[var(--radius-control)] border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-[var(--radius-control)] bg-brand-700 px-4 py-2.5 font-semibold text-white hover:bg-brand-800 disabled:opacity-60"
        >
          {busy ? t('common.loading') : t('auth.resetAction')}
        </button>
      </form>
    </div>
  );
}

/** The shared loading/done/failed shell for the two link-landing pages. */
function Outcome({
  title,
  message,
  tone,
  busy = false,
  children,
}: {
  title: string;
  message: string | null;
  tone: 'info' | 'error';
  busy?: boolean;
  children?: React.ReactNode;
}): React.ReactNode {
  return (
    <div className="mx-auto max-w-md px-4 py-16 text-center">
      <h1 className="text-2xl font-bold">{title}</h1>
      {message && (
        <p
          role={tone === 'error' ? 'alert' : 'status'}
          className={`mt-3 text-sm ${
            tone === 'error'
              ? 'text-red-700 dark:text-red-300'
              : 'text-[var(--color-muted-fg)]'
          }`}
        >
          {message}
        </p>
      )}
      {busy && <p className="mt-3 text-sm text-[var(--color-muted-fg)]">…</p>}
      {children && <p className="mt-6 text-sm">{children}</p>}
    </div>
  );
}
